import React from 'react';
import { 
  Table, 
  Button, 
  Card, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  SafetyOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { AdminRole } from './types';

const { Text } = Typography;

interface AdminRolesTabProps {
  availableRoles: AdminRole[];
  editingRole: any;
  setEditingRole: (role: any) => void;
  editingRolePermissions: any;
  setEditingRolePermissions: (role: any) => void;
  isSubmitting: boolean;
  adminRoleForm: any;
  rolePermissionsForm: any;
  handleUpdateRolePermissions: (roleId: string, values: any) => void;
  handleCreateRole: (values: any) => void;
  handleUpdateRole: (id: string, values: any) => void;
  refetchRoles: () => void;
}

const AdminRolesTab: React.FC<AdminRolesTabProps> = ({
  availableRoles,
  editingRole,
  setEditingRole,
  editingRolePermissions,
  setEditingRolePermissions,
  isSubmitting,
  adminRoleForm,
  rolePermissionsForm,
  handleUpdateRolePermissions,
  handleCreateRole,
  handleUpdateRole,
  refetchRoles
}) => {
  const { t } = useTranslation();

  const roleColumns = [
    {
      title: t('adminUsers.roleName'),
      key: 'name',
      render: (role: AdminRole) => (
        <div>
          <div style={{ fontWeight: 500 }}>{role.name}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{role.description}</Text>
        </div>
      ),
    },
    {
      title: t('adminUsers.userCount'),
      key: 'user_count',
      render: (role: AdminRole) => (
        <Tag color="blue">{role.user_count} {t('adminUsers.users')}</Tag>
      ),
    },
    {
      title: t('adminUsers.systemRole'),
      key: 'is_system_role',
      render: (role: AdminRole) => (
        <Tag color={role.is_system_role ? 'red' : 'green'}>
          {role.is_system_role ? t('adminUsers.systemRole') : t('adminUsers.customRole')}
        </Tag>
      ),
    },
    {
      title: t('common.status'),
      key: 'is_active',
      render: (role: AdminRole) => (
        <Tag color={role.is_active ? 'success' : 'warning'}>
          {role.is_active ? t('adminUsers.statuses.active') : t('adminUsers.statuses.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (role: AdminRole) => (
        <Space>
          <Tooltip title={t('adminUsers.editRole')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => setEditingRole(role)}
            />
          </Tooltip>
          <Tooltip title={t('adminUsers.editPermissions')}>
            <Button
              type="text"
              icon={<SafetyOutlined />}
              onClick={() => setEditingRolePermissions(role)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRole({});
              adminRoleForm.resetFields();
            }}
          >
            {t('adminUsers.addRole')}
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          columns={roleColumns}
          dataSource={availableRoles}
          rowKey="id"
          pagination={{
            total: availableRoles.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('adminUsers.paginationInfo', { start: range[0], end: range[1], total }),
          }}
        />
      </Card>
    </div>
  );
};

export default AdminRolesTab;
